import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { MobType, VillagerJob } from './types'
import type { MobData } from './types'
import { buildSheepGeometry, buildChickenGeometry, buildVillagerGeometry, buildHorseGeometry, buildWolfGeometry } from './geometry'
import { spawnAllMobs } from './spawning'
import { MobSimulation } from './simulation'
import { setAllMobs } from './mobRegistry'

// ─── Shared temp objects ────────────────────────────────────────────
const _mat4 = new THREE.Matrix4()
const _pos = new THREE.Vector3()
const _quat = new THREE.Quaternion()
const _scale = new THREE.Vector3()
const _camDir = new THREE.Vector3()
const _right = new THREE.Vector3()
const _fwd = new THREE.Vector3()
const _upV = new THREE.Vector3()
const _rotMat = new THREE.Matrix4()

// Build orientation using the same tangent frame as movement
// so local +Z (forward) aligns with the heading direction
function buildOrientation(
  quat: THREE.Quaternion,
  nx: number, ny: number, nz: number,
  heading: number,
) {
  // Tangent frame (must match simulation.ts / moveMob)
  let ex: number, ey: number, ez: number
  if (Math.abs(ny) < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el
  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex

  // Forward = heading direction in tangent plane
  const cosH = Math.cos(heading), sinH = Math.sin(heading)
  _fwd.set(
    ex * sinH + fx * cosH,
    ey * sinH + fy * cosH,
    ez * sinH + fz * cosH,
  )

  // Right = cross(up, forward) for right-handed basis
  _upV.set(nx, ny, nz)
  _right.crossVectors(_upV, _fwd)

  // Build rotation matrix: columns = right, up, forward
  _rotMat.makeBasis(_right, _upV, _fwd)
  quat.setFromRotationMatrix(_rotMat)
}

// ─── Vertex shader animation chunk (injected via onBeforeCompile) ───
const ANIM_VERTEX_PARS = /* glsl */ `
  attribute float bodyPart;
  attribute float aAnimPhase;

  // Body part IDs (must match types.ts constants)
  #define PART_BODY    0.0
  #define PART_LEG_BL  1.0
  #define PART_LEG_BR  2.0
  #define PART_LEG_FL  3.0
  #define PART_LEG_FR  4.0
  #define PART_HEAD    5.0
  #define PART_TAIL    6.0
  #define PART_ARM_L   7.0
  #define PART_ARM_R   8.0
`

const ANIM_VERTEX_TRANSFORM = /* glsl */ `
  // Procedural animation based on aAnimPhase
  float phase = aAnimPhase * 6.283185;
  float partId = bodyPart + 0.5; // offset for float comparison

  vec3 animOffset = vec3(0.0);

  if (partId > 0.5 && partId < 4.9) {
    // Legs: swing forward/back (Z axis) with stagger
    float legSign = 1.0;
    float legPhaseOffset = 0.0;

    if (partId < 1.5) { // LEG_BL
      legSign = 1.0; legPhaseOffset = 0.0;
    } else if (partId < 2.5) { // LEG_BR
      legSign = 1.0; legPhaseOffset = 3.14159;
    } else if (partId < 3.5) { // LEG_FL
      legSign = 1.0; legPhaseOffset = 3.14159;
    } else { // LEG_FR
      legSign = 1.0; legPhaseOffset = 0.0;
    }

    float swing = sin(phase + legPhaseOffset) * 0.3;
    animOffset.z += swing;
    // Slight lift at mid-swing
    animOffset.y += max(0.0, sin(phase + legPhaseOffset)) * 0.1;
  }
  else if (partId > 4.9 && partId < 5.5) {
    // Head: gentle bob
    animOffset.y += sin(phase * 2.0) * 0.05;
    animOffset.z += sin(phase) * 0.03;
  }
  else if (partId > 5.9 && partId < 6.5) {
    // Tail: wag
    animOffset.x += sin(phase * 1.5) * 0.08;
  }
  else if (partId > 6.9 && partId < 7.5) {
    // ARM_L: swing opposite to right leg
    animOffset.z += sin(phase) * 0.2;
  }
  else if (partId > 7.9 && partId < 8.5) {
    // ARM_R: swing opposite to left leg
    animOffset.z += sin(phase + 3.14159) * 0.2;
  }

  transformed += animOffset * aAnimPhase; // scale by phase (0 when idle)
`

// ─── Create animated material ───────────────────────────────────────
function createAnimatedMaterial(): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.88,
    metalness: 0.02,
    flatShading: true,
  })

  mat.onBeforeCompile = (shader) => {
    // Inject animation attributes and uniforms
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      '#include <common>\n' + ANIM_VERTEX_PARS,
    )

    // Inject animation transform after the normal vertex transforms
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      '#include <begin_vertex>\n' + ANIM_VERTEX_TRANSFORM,
    )
  }

  return mat
}

// ─── Instanced mob group renderer ───────────────────────────────────
function MobInstances({
  mobs,
  geometry,
  scale,
  simulation,
}: {
  mobs: MobData[]
  geometry: THREE.BufferGeometry
  scale: number
  simulation: MobSimulation
}) {
  const meshRef = useRef<THREE.InstancedMesh>(null)
  const count = mobs.length

  // Create animated material and per-instance animPhase attribute
  const { material, animPhaseAttr } = useMemo(() => {
    const material = createAnimatedMaterial()
    const animPhaseArray = new Float32Array(count)
    const animPhaseAttr = new THREE.InstancedBufferAttribute(animPhaseArray, 1)
    return { material, animPhaseAttr }
  }, [count])

  // Attach animPhase attribute to geometry
  useMemo(() => {
    geometry.setAttribute('aAnimPhase', animPhaseAttr)
  }, [geometry, animPhaseAttr])

  useFrame(({ camera }) => {
    const mesh = meshRef.current
    if (!mesh || count === 0) return

    // Camera direction for hemisphere culling
    const camPos = camera.position
    const camLen = Math.sqrt(camPos.x * camPos.x + camPos.y * camPos.y + camPos.z * camPos.z)
    if (camLen < 0.001) return
    _camDir.set(camPos.x / camLen, camPos.y / camLen, camPos.z / camLen)

    // Pass camera position to simulation for flee behavior
    simulation.cameraNx = _camDir.x
    simulation.cameraNy = _camDir.y
    simulation.cameraNz = _camDir.z

    _scale.set(scale, scale, scale)
    const zeroScale = new THREE.Vector3(0, 0, 0)

    for (let i = 0; i < count; i++) {
      const mob = mobs[i]

      // Hemisphere culling: skip mobs on far side of planet
      const dot = mob.nx * _camDir.x + mob.ny * _camDir.y + mob.nz * _camDir.z
      if (dot < -0.1) {
        _mat4.compose(_pos.set(0, 0, 0), _quat.identity(), zeroScale)
        mesh.setMatrixAt(i, _mat4)
        animPhaseAttr.array[i] = 0
        continue
      }

      // World position on sphere surface
      _pos.set(
        mob.nx * mob.radius,
        mob.ny * mob.radius,
        mob.nz * mob.radius,
      )

      // Orientation
      buildOrientation(_quat, mob.nx, mob.ny, mob.nz, mob.heading)

      _mat4.compose(_pos, _quat, _scale)
      mesh.setMatrixAt(i, _mat4)

      // Per-instance animation phase for vertex shader
      animPhaseAttr.array[i] = mob.animSpeed > 0.01 ? mob.animPhase : 0
    }

    mesh.instanceMatrix.needsUpdate = true
    animPhaseAttr.needsUpdate = true
  })

  if (count === 0) return null

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, count]}
      frustumCulled={false}
      castShadow
      receiveShadow
    />
  )
}

// ─── Main mob renderer component ────────────────────────────────────
interface MobRendererProps {
  maxVillages?: number
}

export function MobRenderer({ maxVillages = 30 }: MobRendererProps) {
  // Spawn all mobs and create simulation once (deterministic)
  const { simulation, sheep, chickens, wolves, villagerGroups, horseGroups } = useMemo(() => {
    const { mobs: allMobs, villages } = spawnAllMobs(maxVillages)
    const simulation = new MobSimulation(allMobs, villages)

    const sheep = allMobs.filter(m => m.type === MobType.Sheep)
    const chickens = allMobs.filter(m => m.type === MobType.Chicken)
    const horses = allMobs.filter(m => m.type === MobType.Horse)
    const wolves = allMobs.filter(m => m.type === MobType.Wolf)

    const villagerGroups = new Map<number, MobData[]>()
    for (const mob of allMobs) {
      if (mob.type !== MobType.Villager) continue
      const group = villagerGroups.get(mob.subType) ?? []
      group.push(mob)
      villagerGroups.set(mob.subType, group)
    }

    // Group horses by color variant for instancing
    const horseGroups = new Map<number, MobData[]>()
    for (const mob of horses) {
      const group = horseGroups.get(mob.subType) ?? []
      group.push(mob)
      horseGroups.set(mob.subType, group)
    }

    setAllMobs(allMobs)
    return { simulation, sheep, chickens, horses, wolves, villagerGroups, horseGroups, allMobs }
  }, [maxVillages])

  // Run simulation every frame
  useFrame(({ clock }, delta) => {
    simulation.update(clock.elapsedTime, delta)
  })

  // Build geometries once
  const sheepGeo = useMemo(() => buildSheepGeometry(), [])
  const chickenGeo = useMemo(() => buildChickenGeometry(), [])
  const wolfGeo = useMemo(() => buildWolfGeometry(), [])

  const villagerGeos = useMemo(() => {
    const geos = new Map<number, THREE.BufferGeometry>()
    for (const job of villagerGroups.keys()) {
      geos.set(job, buildVillagerGeometry(job as VillagerJob))
    }
    return geos
  }, [villagerGroups])

  const horseGeos = useMemo(() => {
    const geos = new Map<number, THREE.BufferGeometry>()
    for (const variant of horseGroups.keys()) {
      geos.set(variant, buildHorseGeometry(variant))
    }
    return geos
  }, [horseGroups])

  return (
    <group>
      <MobInstances mobs={sheep} geometry={sheepGeo} scale={1.2} simulation={simulation} />
      <MobInstances mobs={chickens} geometry={chickenGeo} scale={1.2} simulation={simulation} />
      <MobInstances mobs={wolves} geometry={wolfGeo} scale={1.4} simulation={simulation} />

      {[...horseGroups.entries()].map(([variant, mobs]) => {
        const geo = horseGeos.get(variant)
        if (!geo || mobs.length === 0) return null
        return (
          <MobInstances key={`horse_${variant}`} mobs={mobs} geometry={geo} scale={1.8} simulation={simulation} />
        )
      })}

      {[...villagerGroups.entries()].map(([job, mobs]) => {
        const geo = villagerGeos.get(job)
        if (!geo || mobs.length === 0) return null
        return (
          <MobInstances key={job} mobs={mobs} geometry={geo} scale={1.6} simulation={simulation} />
        )
      })}
    </group>
  )
}
